import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "../useApiClient";
import type { components } from "../schema";

export const STATEMENT_CONSENT_QUERY_KEY = ["statement-consent"] as const;

export type StatementConsentStatusResponse =
  components["schemas"]["StatementConsentStatusResponse"];

/**
 * The signed-in user's statement-collection consent status (L0-U13, ADR 0028
 * D4): the current required version, whether they have accepted it, and when.
 * The import page reads this to decide whether to show the consent disclosure
 * before allowing a real broker statement to be uploaded.
 */
export function useStatementConsent(options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: STATEMENT_CONSENT_QUERY_KEY,
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const { data, error } = await client.GET("/api/consent/statement");
      if (error !== undefined) {
        throw new Error("Failed to load statement-collection consent status");
      }
      return data;
    },
  });
}

/**
 * Record the signed-in user's statement-collection consent at the current
 * version (server-owned; no body is sent). The endpoint returns the refreshed
 * status, which is written straight into the query cache so the import page
 * re-evaluates without a second round trip.
 */
export function useRecordStatementConsent() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST("/api/consent/statement");
      if (error !== undefined) {
        throw new Error("Failed to record statement-collection consent");
      }
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(STATEMENT_CONSENT_QUERY_KEY, data);
    },
  });
}
